import * as React from "react";
import { useGetIdentity, useLogout } from "@refinedev/core";
import type { AuthIdentity } from "@aos/shared";

import { cn } from "@/lib/utils";
import { Icon, type IconName } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useTheme, type Theme } from "@/components/layout/theme-provider";
import { SidebarNav } from "@/components/layout/Sidebar";

/**
 * Event name the command palette (Frontend D6) listens for to open itself.
 * Dispatched when the user clicks the global search box, so the header stays
 * decoupled from the cmdk palette implementation.
 */
export const OPEN_COMMAND_MENU_EVENT = "aos:open-command-menu";

export function openCommandMenu(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OPEN_COMMAND_MENU_EVENT));
  }
}

/**
 * Event the Assistant rail listens for to toggle itself (design-system §4.3 /
 * frontend-display §4). The Header stays decoupled from the rail surface — the
 * rail is wired in a later slice; here the affordance just dispatches `⌘J`.
 */
export const TOGGLE_ASSISTANT_EVENT = "aos:toggle-assistant";

export function toggleAssistant(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(TOGGLE_ASSISTANT_EVENT));
  }
}

export function Header() {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // ⌘J / Ctrl-J toggles the Assistant rail (the surface is wired in a later
  // slice; here the shortcut just dispatches the toggle event).
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "j" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggleAssistant();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    // §4.2 sticky top bar: slim (h-12) so page content dominates the viewport,
    // translucent paper, a single bottom hairline (the only border — a
    // structural fold, §3.1 rule 4).
    <header className="sticky top-0 z-30 flex h-12 shrink-0 items-center gap-3 border-b border-border bg-background/80 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:px-6">
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 md:hidden"
            aria-label="Open navigation"
          >
            <Icon name="menu" size={20} aria-hidden />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-60 p-0">
          <SidebarNav onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="ml-auto flex items-center gap-1">
        <GlobalSearch />
        <AssistantToggle />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}

/**
 * The primary navigator — a single small ghost icon button on the right of the
 * top bar (alongside the assistant / theme / user controls). It opens the
 * command palette (also bound to ⌘K). Kept compact + minimal per the design
 * system rather than a wide faux search input.
 */
function GlobalSearch() {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={openCommandMenu}
      className="h-9 w-9"
      aria-label="Search"
      title="Search · ⌘K"
    >
      <Icon name="search" size={18} aria-hidden />
    </Button>
  );
}

/**
 * Assistant rail toggle (§4.3). The rail surface is mounted in a later slice;
 * this affordance dispatches the toggle event and pairs with `⌘J`.
 */
function AssistantToggle() {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-9 w-9"
      aria-label="Toggle assistant"
      title="Assistant (⌘J)"
      onClick={toggleAssistant}
    >
      <Icon name="assistant" size={20} aria-hidden />
    </Button>
  );
}

function ThemeToggle() {
  const { theme, resolvedTheme, setTheme } = useTheme();

  const options: { value: Theme; label: string; icon: IconName }[] = [
    { value: "light", label: "Light", icon: "themeLight" },
    { value: "dark", label: "Dark", icon: "themeDark" },
    { value: "system", label: "System", icon: "themeSystem" },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          aria-label="Toggle theme"
        >
          {resolvedTheme === "dark" ? (
            <Icon name="themeDark" size={20} aria-hidden />
          ) : (
            <Icon name="themeLight" size={20} aria-hidden />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {options.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            onSelect={() => setTheme(opt.value)}
            className={cn(theme === opt.value && "bg-accent")}
          >
            <Icon name={opt.icon} size={16} aria-hidden />
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function initialsOf(name: string | undefined, email: string | undefined): string {
  const source = (name ?? email ?? "").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0]?.[0] ?? "";
    const b = parts[1]?.[0] ?? "";
    return (a + b).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

function UserMenu() {
  const { data: identity } = useGetIdentity<AuthIdentity>();
  const { mutate: logout } = useLogout();

  const displayName = identity?.display_name;
  const email = identity?.email;
  const initials = initialsOf(displayName, email);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 rounded-full"
          aria-label="User menu"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={undefined} alt={displayName ?? email ?? "User"} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-0.5">
            <span className="truncate text-sm font-medium">
              {displayName ?? "Signed in"}
            </span>
            {email ? (
              <span className="truncate text-xs font-normal text-muted-foreground">
                {email}
              </span>
            ) : null}
            {identity?.roles?.length ? (
              <span className="truncate text-xs font-normal text-muted-foreground">
                {identity.roles.join(", ")}
              </span>
            ) : null}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled>
          <Icon name="user" size={16} aria-hidden />
          Profile
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => logout()}>
          <Icon name="logout" size={16} aria-hidden />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
